import React, { useState } from 'react';
import {
  DragEndEvent,
  TouchSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arraySwap } from '@dnd-kit/sortable';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { useTranslation } from 'react-i18next';
import { AdImage } from '@/components/AdImages/AdImages.types';
import { CONFIG } from '@/constants/config';
import { UploadImageResponse } from '@/types';
import { useMobileDetect } from '@/hooks/useMobileDetect';

interface Props {
  onChange: (callback: (prev: AdImage[]) => AdImage[]) => void;
  onError?: () => void;
}

export const useImagesList = ({ onChange, onError }: Props) => {
  const [sourceObject, setSourceObject] = useState<
    Record<string, CancelTokenSource>
  >({});
  const { isDesktop } = useMobileDetect();
  const { i18n } = useTranslation();

  const pointerSensor = useSensor(PointerSensor);
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });

  const sensors = useSensors(isDesktop ? pointerSensor : touchSensor);

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      onChange(prev => {
        const oldIndex = prev.findIndex(el => el?.id === active.id);
        const newIndex = prev.findIndex(el => el?.id === over.id);
        if (oldIndex < 0 || newIndex < 0) {
          return prev;
        }
        return arraySwap(prev, oldIndex, newIndex);
      });
    }
  }

  const onRemove = (id: string) => {
    if (sourceObject[id]) {
      sourceObject[id].cancel('Operation canceled by the user.');
    }
    onChange(prev => prev.filter(el => el?.id !== id));
  };

  const handleRotate = (imageId: string, angle: number) => {
    axios
      .post<{ id: string; thumbnail_url: string }>(
        `https://${CONFIG.photosBaseUrl}/pond/rotate`,
        null,
        {
          params: {
            id: imageId,
            angle,
            lang: i18n.language,
          },
          headers: {
            Accept: 'application/json',
          },
        }
      )
      .then(response => {
        const { id, thumbnail_url: src } = response.data;
        onChange(prev =>
          prev.map(image => (image?.id === imageId ? { id, src } : image))
        );
      });
  };

  const catchErrors = (imageId: string) => (error: AxiosError) => {
    if (!axios.isCancel(error)) {
      onError?.();
      onChange(prev => prev.filter(el => el?.id !== imageId));
    }
  };

  const handleUpload = (formData: FormData, imageId: string) => {
    const source = axios.CancelToken.source();
    setSourceObject(prevState => ({ ...prevState, [imageId]: source }));
    axios
      .post<UploadImageResponse>(
        `https://${CONFIG.photosBaseUrl}/pond`,
        formData,
        {
          params: { lang: i18n.language },
          cancelToken: source.token,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      .then(response => {
        const [{ id, thumbnail_url: src }] = response.data.photos;
        onChange(prev =>
          prev.map(item =>
            item?.id === imageId ? { id, src, isUploading: false } : item
          )
        );
      })
      .catch(catchErrors(imageId));
  };

  function onFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (!files) return;

    const newImages = Array.from(files).reduce<AdImage[]>(
      (acc, file, index) => {
        const tempId = `${index}-loading`;
        const mockImage = {
          id: tempId,
          src: '',
          isUploading: true,
        };
        acc.push(mockImage);
        const formData = new FormData();
        formData.append('images[]', file);
        handleUpload(formData, tempId);
        return acc;
      },
      []
    );

    onChange(prev => [...prev, ...newImages]);
  }

  return {
    sensors,
    onDragEnd,
    onRemove,
    onFileInputChange,
    handleRotate,
  };
};
